import { RightOutlined } from '@ant-design/icons'
import { Link } from '@tanstack/react-router'
import { Badge, Card, Empty, Flex, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { WhatsappBusinessAccount } from '../../whatsapp-business-account'

export type WhatsappBusinessAccountGridProps = {
    accounts: WhatsappBusinessAccount[]
}

// The index reads like a switchboard: pick a connected WhatsApp line to manage.
// Each card mirrors the detail page's identity (green tile + name + Connected).
export function WhatsappBusinessAccountGrid({ accounts }: WhatsappBusinessAccountGridProps) {
    const { t } = useTranslation()

    if (accounts.length === 0) {
        return (
            <Flex
                justify="center"
                className="py-16"
            >
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('No WhatsApp business accounts found')}
                />
            </Flex>
        )
    }

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {accounts.map((account) => (
                <Link
                    key={account.id}
                    to="/whatsapp-business-accounts/$accountId"
                    params={{ accountId: account.id }}
                    className="block h-full"
                >
                    <Card
                        size="small"
                        className="h-full shadow-xs transition-colors hover:border-primary/40"
                    >
                        <Flex
                            gap="middle"
                            align="flex-start"
                        >
                            <Flex
                                vertical
                                gap={6}
                                className="min-w-0 flex-1"
                            >
                                <Flex
                                    align="center"
                                    justify="space-between"
                                    gap="small"
                                >
                                    <Typography.Title
                                        level={5}
                                        className="mb-0 truncate"
                                    >
                                        {account.name}
                                    </Typography.Title>
                                    <RightOutlined className="shrink-0 text-xs text-ink-quaternary" />
                                </Flex>

                                <Badge
                                    status="success"
                                    text={t('Connected')}
                                />

                                <Flex
                                    vertical
                                    gap={1}
                                    className="min-w-0"
                                >
                                    <span className="text-[10px] font-medium uppercase tracking-wide text-ink-tertiary">
                                        {t('WhatsApp ID')}
                                    </span>
                                    <span className="truncate font-mono text-[13px] text-ink-secondary">
                                        {account.whatsappId}
                                    </span>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Card>
                </Link>
            ))}
        </div>
    )
}
